import { format, getMonth, getYear } from "date-fns";
import React, { useState } from "react";

import {
  Box,
  Center,
  Text,
  Pressable,
} from "@whoosh/design-system/src/components/primitive";
import { WhooshThemeColors } from "@whoosh/design-system/src/theme/restyle/theme";

import { getStringifiedDate } from "../utils/calendarDateUtils";

type WeekDayProps = {
  monthDateObj: Date;
  curDateObj: Date;
  endDate: string;
  onDatePress: (date: string) => void;
  selectedDate?: string;
};

export const WeekDay = (props: WeekDayProps) => {
  const { curDateObj, monthDateObj, endDate, onDatePress, selectedDate } =
    props;
  const now = new Date();

  const today = getStringifiedDate(now);
  const displayedDate = getStringifiedDate(curDateObj);

  const isDateBeforeToday = displayedDate < today;
  const curDateIsToday = today === displayedDate;
  const isPastEndDate = displayedDate > endDate;

  const isDateInCurrentMonth =
    getMonth(curDateObj) === getMonth(monthDateObj) &&
    getYear(curDateObj) === getYear(monthDateObj);

  const isSelected = selectedDate === displayedDate;
  const isDisabled = isDateBeforeToday || isPastEndDate;

  const [hovering, setHovering] = useState(false);
  const [pressing, setPressing] = useState(false);

  const indicatorBgColor: WhooshThemeColors = isSelected
    ? "button-main-surface-neutral"
    : pressing
    ? "grayscale-500"
    : hovering
    ? "grayscale-200"
    : "transparent";

  return (
    <Center>
      {isDateInCurrentMonth ? (
        <Pressable
          onPress={() => {
            onDatePress(displayedDate);
          }}
          disabled={isDisabled}
          testID={`calendar-day-${displayedDate}`}
          position="relative"
          onHoverIn={() => setHovering(true)}
          onHoverOut={() => setHovering(false)}
          onPressIn={() => setPressing(true)}
          onPressOut={() => setPressing(false)}
          alignItems="center"
          justifyContent="center"
          accessibilityRole="button"
          accessibilityState={{
            selected: isSelected,
            disabled: isDisabled,
          }}
        >
          <Box
            bg={indicatorBgColor}
            width={{ base: 36, xs: 40 }}
            height={{ base: 36, xs: 40 }}
            position="absolute"
            style={{
              borderRadius: 8,
            }}
            borderWidth={!isSelected && curDateIsToday ? 1 : 0}
            borderColor={
              !isSelected && curDateIsToday ? "grayscale-500" : "transparent"
            }
          />

          <Center
            width={{ base: 36, xs: 40 }}
            height={{ base: 36, xs: 40 }}
            alignItems="center"
            justifyContent="center"
            borderRadius={"4"}
            bg={isSelected ? "button-main-surface-neutral" : "transparent"}
          >
            <Text
              color={
                isDateBeforeToday || isPastEndDate
                  ? "text-disabled"
                  : isSelected
                  ? "text-inverse"
                  : "text-primary"
              }
              variant="mainBaseMedium"
              maxFontSizeMultiplier={1}
            >
              {format(curDateObj, "d")}
            </Text>
          </Center>
        </Pressable>
      ) : (
        <Center
          width={{ base: 36, xs: 40 }}
          height={{ base: 36, xs: 40 }}
          bg="transparent"
        />
      )}
    </Center>
  );
};
